import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Alert,
  Switch,
  TextField,
  Typography,
  Chip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import PublicIcon from '@mui/icons-material/Public';
import moment from 'moment';
import DatePicker from '../../shared/components/forms/HotelDatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import countriesData from '../../data/countries-states-cities-data';
import countryCodesData from '../../data/country-codes.json';
import { countryFilterOptions, stateFilterOptions, cityFilterOptions } from '../../utils/locationSearch';
import {
  formatAadhaarDisplay,
  getDocumentNumberPlaceholder,
  isAadhaarDocumentType,
  normalizeDocumentIdForSubmit,
  normalizeDocumentIdInput,
  validateDocumentIdNumber,
} from '../../utils/documentIdFormatters';
import {
  formatPhoneLengthLabel,
  normalizePhoneInputForCountry,
} from '../../utils/phoneValidation';
import { captureNativeCameraDataUrl, getNativeCameraErrorMessage } from '../../utils/nativeCameraCapture';
import { getBookingGuestDocuments } from '../../services/api';
import ForeignGuestDetailsDrawer from '../booking/ForeignGuestDetailsDrawer';
import { resolveHotelCountryName } from '../booking/BookingGuestFormDrawer';
import {
  EMPTY_FOREIGN_GUEST_DETAILS,
  applyForeignCountryChange,
  foreignDetailsToGuestApiPayload,
  isForeignGuestCountry,
  mapGuestApiToForeignDetails,
  validateForeignGuestDetails,
} from '../../utils/foreignGuestFormUtils';
import {
  BOOKING_DRAWER_ACCENT,
  BOOKING_DRAWER_WIDTH_SX,
  createBookingDrawerOverlayProps,
  getBookingDrawerFooterSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerPaperSx,
  getBookingDrawerTheme,
  getGuestDrawerContentSx,
  getGuestDrawerFieldSx,
  getGuestDrawerSelectSx,
  getGuestDrawerUploadPaperSx,
} from '../../utils/bookingDrawerLayout';

const TITLE_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'Mr', label: 'Mr' },
  { value: 'Mrs', label: 'Mrs' },
  { value: 'Ms', label: 'Ms' },
  { value: 'Dr', label: 'Dr' },
  { value: 'Prof', label: 'Prof' },
  { value: 'Sir', label: 'Sir' },
  { value: 'Madam', label: 'Madam' },
];

const GENDERS = [
  { value: '', label: '—' },
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];

const DOCUMENT_OPTIONS = [
  { value: '', label: '—' },
  { value: 'aadhaar', label: 'Aadhaar Card' },
  { value: 'passport', label: 'Passport' },
  { value: 'voter_id', label: 'Voter ID (EPIC)' },
  { value: 'driving_license', label: 'Driving Licence' },
  { value: 'pan', label: 'PAN Card' },
  { value: 'government_id', label: 'Government Issued ID' },
  { value: 'oci', label: 'OCI Card' },
  { value: 'pio', label: 'PIO Card' },
  { value: 'student_id', label: 'Student ID' },
  { value: 'company_id', label: 'Corporate / Company ID' },
  { value: 'other', label: 'Other Document' },
];

function parseFullName(fullName) {
  if (!fullName || !fullName.trim()) return { first_name: '', middle_name: '', last_name: '' };
  const parts = fullName.trim().replace(/\s+/g, ' ').split(' ').filter(Boolean);
  if (parts.length === 1) return { first_name: parts[0], middle_name: '', last_name: '' };
  if (parts.length === 2) return { first_name: parts[0], middle_name: '', last_name: parts[1] };
  if (parts.length === 3) return { first_name: parts[0], middle_name: parts[1], last_name: parts[2] };
  return {
    first_name: parts.slice(0, parts.length - 2).join(' '),
    middle_name: parts[parts.length - 2],
    last_name: parts[parts.length - 1],
  };
}

function buildName(customer) {
  return [customer?.first_name, customer?.middle_name, customer?.last_name].filter(Boolean).join(' ').trim();
}

function parsePhoneAndCountry(phoneStr, fallbackCode = '+91') {
  if (!phoneStr || typeof phoneStr !== 'string') return { phone: '', country_code: fallbackCode };
  const str = phoneStr.trim();
  if (str.startsWith('+')) {
    const m = str.match(/^(\+\d{1,3})(\d+)$/);
    if (m) return { country_code: m[1], phone: m[2].replace(/\D/g, '') };
  }
  return { phone: str.replace(/\D/g, ''), country_code: fallbackCode };
}

function buildFormFromCustomer(customer) {
  const fallbackCode = customer?.country_code || '+91';
  const parsed = parsePhoneAndCountry(customer?.phone || '', fallbackCode);
  const phone = String(customer?.phone || parsed.phone || '').replace(/\D/g, '');
  const idNumber = isAadhaarDocumentType(customer?.document_type)
    ? formatAadhaarDisplay(customer?.id_number)
    : String(customer?.id_number || '');
  return {
    title: customer?.title || '',
    name: buildName(customer),
    first_name: customer?.first_name || '',
    middle_name: customer?.middle_name || '',
    last_name: customer?.last_name || '',
    email: customer?.email || '',
    phone,
    country_code: customer?.country_code || parsed.country_code || '+91',
    alternate_phone: String(customer?.alternate_phone || '').replace(/\D/g, ''),
    address: customer?.address_line1 || '',
    city: customer?.city || '',
    state: customer?.state || '',
    country: customer?.country || '',
    pincode: customer?.pincode || '',
    dob: customer?.dob ? String(customer.dob).slice(0, 10) : '',
    gender: customer?.gender || '',
    notes: customer?.notes || '',
    document_type: customer?.document_type || '',
    id_number: idNumber,
    camera_upload: false,
    id_front: customer?.id_front_url || '',
    id_back: customer?.id_back_url || '',
  };
}

export default function CustomerContactInfoDrawer({
  open,
  onClose,
  customer,
  initialMode = 'view',
  onSave,
  saving = false,
  allowEdit = true,
  titleOverride = null,
  subtitleOverride = null,
  bookingId = null,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accent = BOOKING_DRAWER_ACCENT;
  const drawerTheme = getBookingDrawerTheme(isDark);
  const overlays = useMemo(
    () => createBookingDrawerOverlayProps(isDark, isDark ? drawerTheme.surface : '#ffffff'),
    [isDark, drawerTheme.surface]
  );
  const fieldSx = useMemo(() => getGuestDrawerFieldSx(isDark), [isDark]);
  const selectSx = useMemo(() => getGuestDrawerSelectSx(isDark), [isDark]);
  const contentSx = useMemo(() => getGuestDrawerContentSx(isDark), [isDark]);

  const phoneCountryCodeAnchorRef = useRef(null);
  const titleMenuAnchorRef = useRef(null);
  const fileInputFrontRef = useRef(null);
  const fileInputBackRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState(() => buildFormFromCustomer(customer));
  const [initialFrontUrl, setInitialFrontUrl] = useState(customer?.id_front_url || '');
  const [initialBackUrl, setInitialBackUrl] = useState(customer?.id_back_url || '');
  const [error, setError] = useState('');
  const [documentNumberError, setDocumentNumberError] = useState('');
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [phoneCountryCodeMenuOpen, setPhoneCountryCodeMenuOpen] = useState(false);
  const [phoneCountryCodeSearch, setPhoneCountryCodeSearch] = useState('');
  const [titleMenuOpen, setTitleMenuOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraType, setCameraType] = useState(null);
  const [stream, setStream] = useState(null);
  const [cameraPermissionSnackbar, setCameraPermissionSnackbar] = useState({
    open: false,
    message: '',
  });
  const [foreignGuestDetails, setForeignGuestDetails] = useState(EMPTY_FOREIGN_GUEST_DETAILS);
  const [foreignGuestDrawerOpen, setForeignGuestDrawerOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setMode(allowEdit ? initialMode : 'view');
    const next = buildFormFromCustomer(customer);
    setForm(next);
    setInitialFrontUrl(customer?.id_front_url || '');
    setInitialBackUrl(customer?.id_back_url || '');
    setError('');
    setDocumentNumberError('');
    setDocumentsLoading(false);
    setForeignGuestDetails(mapGuestApiToForeignDetails(customer || {}, next.country));
    setForeignGuestDrawerOpen(false);
  }, [open, initialMode, customer, allowEdit]);

  useEffect(() => {
    if (!open || !bookingId || !customer?.id) return;
    // Only fetch when stay docs exist and URLs are not already on the payload.
    const hasDocFlags = Boolean(customer?.has_id_front || customer?.has_id_back);
    const hasUrls = Boolean(customer?.id_front_url || customer?.id_back_url);
    if (!hasDocFlags || hasUrls) return;

    let cancelled = false;
    setDocumentsLoading(true);
    (async () => {
      try {
        const docData = await getBookingGuestDocuments(bookingId, customer.id);
        if (cancelled || !docData) return;
        const frontUrl = docData.id_front_url || '';
        const backUrl = docData.id_back_url || '';
        if (frontUrl || backUrl || docData.document_type || docData.id_number) {
          setForm((prev) => ({
            ...prev,
            id_front: frontUrl || prev.id_front,
            id_back: backUrl || prev.id_back,
            document_type: docData.document_type || prev.document_type,
            id_number:
              docData.id_number != null && docData.id_number !== ''
                ? normalizeDocumentIdInput(
                    docData.document_type || prev.document_type,
                    docData.id_number
                  )
                : prev.id_number,
          }));
          if (frontUrl) setInitialFrontUrl(frontUrl);
          if (backUrl) setInitialBackUrl(backUrl);
        }
      } catch (_) {
        // Stay guest docs are best-effort; form still shows other fields.
      } finally {
        if (!cancelled) setDocumentsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    bookingId,
    customer?.id,
    customer?.has_id_front,
    customer?.has_id_back,
    customer?.id_front_url,
    customer?.id_back_url,
  ]);

  useEffect(
    () => () => {
      if (stream) stream.getTracks().forEach((track) => track.stop());
    },
    [stream]
  );

  const countryCodes = countryCodesData;
  const selectedPhoneCountryCode = useMemo(
    () =>
      countryCodes.find((cc) => cc.dialCode === (form.country_code || '').trim()) ||
      countryCodes.find((cc) => cc.code === 'IN'),
    [form.country_code, countryCodes]
  );

  const countries = useMemo(
    () => (countriesData.countries || []).map((c) => ({ code: c.code, name: c.name })),
    []
  );
  const selectedCountryObj = useMemo(() => {
    if (!form.country) return null;
    return (countriesData.countries || []).find(
      (c) => c.name === form.country || c.code === form.country
    );
  }, [form.country]);
  const hotelCountry = useMemo(() => resolveHotelCountryName(), []);
  const isForeignGuest = useMemo(
    () => isForeignGuestCountry(form.country, hotelCountry),
    [form.country, hotelCountry]
  );
  const foreignGuestComplete = useMemo(() => {
    if (!isForeignGuest) return true;
    return validateForeignGuestDetails(foreignGuestDetails).ok;
  }, [isForeignGuest, foreignGuestDetails]);
  const availableStates = useMemo(() => {
    if (!selectedCountryObj) return [];
    return (selectedCountryObj.states || []).map((s) => ({ code: s.code, name: s.name }));
  }, [selectedCountryObj]);
  const availableCities = useMemo(() => {
    if (!selectedCountryObj || !form.state) return [];
    const stateObj = (selectedCountryObj.states || []).find(
      (s) => s.name === form.state || s.code === form.state
    );
    return stateObj ? stateObj.cities || [] : [];
  }, [selectedCountryObj, form.state]);

  const handleNameChange = useCallback((value) => {
    const parsed = parseFullName(value);
    setForm((prev) => ({ ...prev, name: value, ...parsed }));
  }, []);

  const isAadhaar = isAadhaarDocumentType(form.document_type);
  const readOnly = mode === 'view';
  const getDocumentTypeLabel = () => {
    if (!form.document_type) return 'Document';
    const opt = DOCUMENT_OPTIONS.find((o) => o.value === form.document_type);
    return opt ? opt.label : 'Document';
  };
  const documentNumberLabel = form.document_type
    ? `${getDocumentTypeLabel().replace(/ Card$/, '')} Number`
    : 'Document number';
  const getFrontDocumentLabel = () => `Upload ${getDocumentTypeLabel()} Front`;
  const getBackDocumentLabel = () => `Upload ${getDocumentTypeLabel()} Back`;

  const resolveDocImagePayload = (current, initialUrl) => {
    if (typeof current === 'string' && current.startsWith('data:image')) return current;
    if (!current && initialUrl) return '';
    return undefined;
  };

  const handleFileUpload = (e, type) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setForm((prev) => ({ ...prev, [type === 'front' ? 'id_front' : 'id_back']: reader.result }));
      };
      reader.readAsDataURL(file);
    }
    e.target.value = '';
  };

  const showCameraPermissionMessage = (message) => {
    setCameraPermissionSnackbar({
      open: true,
      message:
        message ||
        'Camera access was denied or is unavailable. Allow camera permission in your browser settings for this site, or turn off Camera upload to choose a file.',
    });
  };

  const startCamera = async (type) => {
    try {
      const nativeImageData = await captureNativeCameraDataUrl();
      if (nativeImageData) {
        setForm((prev) => ({ ...prev, [type === 'front' ? 'id_front' : 'id_back']: nativeImageData }));
        return;
      }
    } catch (err) {
      const message = getNativeCameraErrorMessage(err);
      if (message) showCameraPermissionMessage(message);
      return;
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      showCameraPermissionMessage(
        'Camera is not available in this browser or page context. Turn off Camera upload to upload a file instead.'
      );
      return;
    }
    try {
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
      }
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } },
      });
      setStream(mediaStream);
      setCameraType(type);
      setCameraOpen(true);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = mediaStream;
          videoRef.current.play().catch(() => {});
        }
      }, 100);
    } catch {
      showCameraPermissionMessage();
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
    setCameraOpen(false);
    setCameraType(null);
  };

  const capturePhoto = () => {
    if (videoRef.current && canvasRef.current) {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      const context = canvas.getContext('2d');
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      context.drawImage(video, 0, 0);
      const imageData = canvas.toDataURL('image/jpeg', 0.8);
      setForm((prev) => ({
        ...prev,
        [cameraType === 'front' ? 'id_front' : 'id_back']: imageData,
      }));
      stopCamera();
    }
  };

  const handleUploadClick = (type) => {
    if (readOnly) return;
    if (form.camera_upload) {
      startCamera(type);
    } else if (type === 'front') {
      fileInputFrontRef.current?.click();
    } else {
      fileInputBackRef.current?.click();
    }
  };

  const handleSave = async () => {
    if (!form.first_name?.trim() && !form.name?.trim()) {
      setError('Name is required');
      return;
    }
    if (!form.phone?.trim()) {
      setError('Mobile number is required');
      return;
    }
    if (isForeignGuest && !foreignGuestComplete) {
      setError('Foreign guest details are required');
      setForeignGuestDrawerOpen(true);
      return;
    }
    const idError = validateDocumentIdNumber(form.document_type, form.id_number);
    if (idError) {
      setDocumentNumberError(idError);
      setError(idError);
      return;
    }
    setDocumentNumberError('');
    setError('');
    const payload = {
      title: form.title || null,
      first_name: (form.first_name || '').trim() || '',
      middle_name: (form.middle_name || '').trim() || null,
      last_name: (form.last_name || '').trim() || '',
      email: (form.email || '').trim() || null,
      country_code: (form.country_code || '').trim() || null,
      phone: (form.phone || '').trim() ? (form.phone || '').trim().replace(/\D/g, '') : null,
      alternate_phone: (form.alternate_phone || '').trim() || null,
      address_line1: (form.address || '').trim() || null,
      address_line2: null,
      city: (form.city || '').trim() || null,
      state: (form.state || '').trim() || null,
      country: (form.country || '').trim() || null,
      pincode: (form.pincode || '').trim() || null,
      dob: form.dob || null,
      gender: (form.gender || '').trim() || null,
      notes: (form.notes || '').trim() || null,
      document_type: (form.document_type || '').trim() || null,
      id_number: form.document_type
        ? normalizeDocumentIdForSubmit(form.document_type, form.id_number) || null
        : null,
    };
    const idFront = resolveDocImagePayload(form.id_front, initialFrontUrl);
    const idBack = resolveDocImagePayload(form.id_back, initialBackUrl);
    if (idFront !== undefined) payload.id_front = idFront;
    if (idBack !== undefined) payload.id_back = idBack;
    if (isForeignGuest) {
      Object.assign(payload, foreignDetailsToGuestApiPayload(foreignGuestDetails));
    }
    try {
      await onSave?.(payload);
      setMode('view');
    } catch (err) {
      setError(err?.response?.data?.error || err?.message || 'Failed to update guest info');
    }
  };

  return (
    <>
    <Drawer
      anchor="right"
      open={open}
      onClose={saving ? undefined : onClose}
      PaperProps={{
        sx: getBookingDrawerPaperSx(isDark, { width: BOOKING_DRAWER_WIDTH_SX }),
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1.5,
            ...getBookingDrawerHeaderSx(isDark),
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PersonIcon sx={{ color: accent, fontSize: 24 }} />
            <Box>
              <Typography
                variant="h6"
                sx={{ color: isDark ? drawerTheme.text : '#333', fontWeight: 700, lineHeight: 1.2 }}
              >
                {mode === 'edit' ? 'Edit guest info' : (titleOverride || 'Guest info')}
              </Typography>
              <Typography variant="caption" sx={{ color: isDark ? drawerTheme.subtleText : '#64748b' }}>
                {subtitleOverride
                  || (allowEdit
                    ? 'Same details as guest details form'
                    : 'Guest details as captured for this stay')}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} disabled={saving} sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', p: 2, ...overlays.fieldShellSx, ...contentSx }}>
          {error ? (
            <Typography variant="body2" color="error" sx={{ mb: 1.5, fontWeight: 600 }}>
              {error}
            </Typography>
          ) : null}

          <Box sx={{ pointerEvents: readOnly ? 'none' : 'auto', opacity: readOnly ? 0.95 : 1 }}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <Grid container spacing={2}>
                {/* Mobile Number */}
                <Grid item xs={12} md={6}>
                  <Box ref={phoneCountryCodeAnchorRef}>
                    <TextField
                      fullWidth
                      label="Mobile Number"
                      required
                      value={form.phone || ''}
                      onChange={(e) => {
                        const digits = normalizePhoneInputForCountry(e.target.value, form.country_code);
                        setForm((f) => ({ ...f, phone: digits }));
                      }}
                      helperText={`Enter ${formatPhoneLengthLabel(form.country_code)}`}
                      inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Box
                              onClick={() => !readOnly && setPhoneCountryCodeMenuOpen(true)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                cursor: readOnly ? 'default' : 'pointer',
                                gap: 0.5,
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                              }}
                            >
                              <Typography component="span" sx={{ fontSize: '1.2rem' }}>
                                {selectedPhoneCountryCode?.flag || '🇮🇳'}
                              </Typography>
                              <Typography component="span" sx={{ fontWeight: 500, fontSize: '0.875rem' }}>
                                {selectedPhoneCountryCode?.dialCode || '+91'}
                              </Typography>
                              <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                ▼
                              </Typography>
                            </Box>
                          </InputAdornment>
                        ),
                      }}
                      sx={fieldSx}
                    />
                  </Box>
                  <Menu
                    anchorEl={phoneCountryCodeAnchorRef.current}
                    open={phoneCountryCodeMenuOpen}
                    onClose={() => {
                      setPhoneCountryCodeMenuOpen(false);
                      setPhoneCountryCodeSearch('');
                    }}
                    PaperProps={{ sx: { maxHeight: 400, width: 300 } }}
                  >
                    <Box sx={{ p: 1 }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search country..."
                        value={phoneCountryCodeSearch}
                        onChange={(e) => setPhoneCountryCodeSearch(e.target.value)}
                        sx={{ mb: 1 }}
                      />
                    </Box>
                    {countryCodes
                      .filter((c) => {
                        if (!phoneCountryCodeSearch) return true;
                        const q = phoneCountryCodeSearch.toLowerCase();
                        return (
                          c.name.toLowerCase().includes(q) ||
                          c.dialCode.includes(q) ||
                          (c.code || '').toLowerCase().includes(q)
                        );
                      })
                      .map((c) => (
                        <MenuItem
                          key={c.code}
                          onClick={() => {
                            const normalizedPhone = normalizePhoneInputForCountry(form.phone, c.dialCode);
                            setForm((f) => ({ ...f, country_code: c.dialCode, phone: normalizedPhone }));
                            setPhoneCountryCodeMenuOpen(false);
                            setPhoneCountryCodeSearch('');
                          }}
                          selected={selectedPhoneCountryCode?.dialCode === c.dialCode}
                        >
                          <ListItemIcon>
                            <Typography sx={{ fontSize: '1.2rem' }}>{c.flag}</Typography>
                          </ListItemIcon>
                          <ListItemText
                            primary={c.dialCode}
                            secondary={c.name}
                            primaryTypographyProps={{ fontWeight: 500 }}
                          />
                        </MenuItem>
                      ))}
                  </Menu>
                </Grid>

                {/* Name with Title */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    required
                    value={form.name || ''}
                    onChange={(e) => handleNameChange(e.target.value)}
                    helperText="Enter full name or first name"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Box
                            ref={titleMenuAnchorRef}
                            onClick={() => !readOnly && setTitleMenuOpen(true)}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              cursor: readOnly ? 'default' : 'pointer',
                              gap: 0.5,
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                            }}
                          >
                            <Typography
                              component="span"
                              sx={{ fontWeight: 500, fontSize: '0.875rem', minWidth: 40 }}
                            >
                              {form.title || 'Title'}
                            </Typography>
                            <Typography component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                              ▼
                            </Typography>
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                    sx={fieldSx}
                  />
                  <Menu
                    anchorEl={titleMenuAnchorRef.current}
                    open={titleMenuOpen}
                    onClose={() => setTitleMenuOpen(false)}
                    PaperProps={{ sx: { maxHeight: 300, minWidth: 150 } }}
                  >
                    {TITLE_OPTIONS.map((opt) => (
                      <MenuItem
                        key={opt.value || 'none'}
                        onClick={() => {
                          setForm((f) => ({ ...f, title: opt.value }));
                          setTitleMenuOpen(false);
                        }}
                        selected={form.title === opt.value}
                      >
                        {opt.value ? opt.label : <em>None</em>}
                      </MenuItem>
                    ))}
                  </Menu>
                </Grid>

                {/* Email | Gender | DOB */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Email Address"
                    type="email"
                    value={form.email || ''}
                    onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                    sx={fieldSx}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth sx={selectSx}>
                    <InputLabel>Gender</InputLabel>
                    <Select
                      value={form.gender || ''}
                      label="Gender"
                      MenuProps={overlays.selectMenuProps}
                      onChange={(e) => setForm((f) => ({ ...f, gender: e.target.value }))}
                    >
                      {GENDERS.map((g) => (
                        <MenuItem key={g.value || 'blank'} value={g.value}>
                          {g.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <DatePicker
                    label="Date of Birth"
                    value={form.dob ? moment(form.dob) : null}
                    onChange={(date) => {
                      if (date && date.isValid() && !date.isAfter(moment())) {
                        setForm((f) => ({ ...f, dob: date.format('YYYY-MM-DD') }));
                      } else if (!date) {
                        setForm((f) => ({ ...f, dob: '' }));
                      }
                    }}
                    maxDate={moment()}
                    slotProps={{
                      ...overlays.datePickerSlotProps,
                      textField: {
                        ...(overlays.datePickerSlotProps?.textField || {}),
                        fullWidth: true,
                        sx: fieldSx,
                      },
                    }}
                  />
                </Grid>

                {/* Guest Address */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Guest Address"
                    value={form.address || ''}
                    onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                    multiline
                    rows={2}
                    sx={fieldSx}
                  />
                </Grid>

                {/* Country / State / City / Pincode */}
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    options={countries}
                    getOptionLabel={(opt) => opt.name || ''}
                    filterOptions={countryFilterOptions}
                    value={countries.find((c) => c.name === form.country || c.code === form.country) || null}
                    onChange={(e, newVal) => {
                      const countryValue = newVal ? newVal.name : '';
                      setForm((f) => ({
                        ...f,
                        country: countryValue,
                        state: '',
                        city: '',
                      }));
                      const next = applyForeignCountryChange(
                        countryValue,
                        hotelCountry,
                        foreignGuestDetails
                      );
                      setForeignGuestDetails(next.details);
                      if (next.shouldOpenDrawer) {
                        setForm((f) => ({
                          ...f,
                          document_type: f.document_type ? f.document_type : 'passport',
                        }));
                        setForeignGuestDrawerOpen(true);
                      } else {
                        setForeignGuestDrawerOpen(false);
                      }
                    }}
                    slotProps={overlays.autocompleteSlotProps}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Country"
                        required
                        helperText={
                          isForeignGuest
                            ? 'Foreign guest details required'
                            : 'Auto-selected from hotel'
                        }
                        sx={fieldSx}
                      />
                    )}
                  />
                  {isForeignGuest && mode === 'edit' ? (
                    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                      <Chip
                        size="small"
                        icon={<PublicIcon sx={{ fontSize: '16px !important' }} />}
                        label={
                          foreignGuestComplete
                            ? 'Foreign guest details saved'
                            : 'Add foreign guest details'
                        }
                        onClick={() => setForeignGuestDrawerOpen(true)}
                        color={foreignGuestComplete ? 'success' : 'warning'}
                        variant={foreignGuestComplete ? 'filled' : 'outlined'}
                        sx={{ fontWeight: 700 }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => setForeignGuestDrawerOpen(true)}
                        aria-label="Edit foreign guest details"
                        sx={{ color: accent }}
                      >
                        <EditIcon sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  ) : null}
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    options={availableStates}
                    getOptionLabel={(opt) => opt.name || ''}
                    filterOptions={stateFilterOptions}
                    value={availableStates.find((s) => s.name === form.state) || null}
                    onChange={(e, newVal) => {
                      setForm((f) => ({
                        ...f,
                        state: newVal ? newVal.name : '',
                        city: '',
                      }));
                    }}
                    disabled={!selectedCountryObj}
                    slotProps={overlays.autocompleteSlotProps}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="State"
                        helperText={selectedCountryObj ? 'Select a state' : 'Select country first'}
                        sx={fieldSx}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Autocomplete
                    options={availableCities}
                    getOptionLabel={(opt) => (typeof opt === 'string' ? opt : '')}
                    filterOptions={cityFilterOptions}
                    value={form.city || null}
                    onChange={(e, newVal) => setForm((f) => ({ ...f, city: newVal || '' }))}
                    freeSolo
                    disabled={!selectedCountryObj || !form.state}
                    slotProps={overlays.autocompleteSlotProps}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="City"
                        helperText={
                          selectedCountryObj && form.state
                            ? 'Select a city'
                            : 'Select state first'
                        }
                        sx={fieldSx}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Pincode"
                    value={form.pincode || ''}
                    onChange={(e) => setForm((f) => ({ ...f, pincode: e.target.value.slice(0, 10) }))}
                    inputProps={{ maxLength: 10 }}
                    sx={fieldSx}
                  />
                </Grid>

                {/* Notes */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={2}
                    value={form.notes || ''}
                    onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                    sx={fieldSx}
                  />
                </Grid>

                {/* Document section — same as Guest Details */}
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={selectSx}>
                    <InputLabel>Select Document type</InputLabel>
                    <Select
                      value={form.document_type || ''}
                      label="Select Document type"
                      MenuProps={overlays.selectMenuProps}
                      onChange={(e) => {
                        const nextType = e.target.value;
                        setForm((f) => ({
                          ...f,
                          document_type: nextType,
                          id_number: nextType ? f.id_number : '',
                        }));
                        setDocumentNumberError(
                          nextType ? validateDocumentIdNumber(nextType, form.id_number) : ''
                        );
                      }}
                    >
                      {DOCUMENT_OPTIONS.map((doc) => (
                        <MenuItem key={doc.value || 'blank'} value={doc.value}>
                          {doc.value ? doc.label : <em>Select document type</em>}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    required={!!form.document_type}
                    disabled={!form.document_type}
                    label={form.document_type ? documentNumberLabel : 'Document number'}
                    placeholder={
                      form.document_type
                        ? getDocumentNumberPlaceholder(form.document_type)
                        : 'Select a document type first'
                    }
                    value={
                      form.document_type
                        ? (isAadhaar ? formatAadhaarDisplay(form.id_number) : (form.id_number || ''))
                        : ''
                    }
                    onChange={(e) => {
                      if (!form.document_type) return;
                      const value = normalizeDocumentIdInput(form.document_type, e.target.value);
                      setForm((f) => ({ ...f, id_number: value }));
                      setDocumentNumberError(validateDocumentIdNumber(form.document_type, value));
                    }}
                    onBlur={() => {
                      if (!form.document_type) return;
                      setDocumentNumberError(
                        validateDocumentIdNumber(form.document_type, form.id_number)
                      );
                    }}
                    error={!!documentNumberError}
                    helperText={documentNumberError || ''}
                    inputProps={{
                      maxLength: isAadhaar ? 14 : undefined,
                      inputMode: isAadhaar ? 'numeric' : undefined,
                      pattern: isAadhaar ? '[0-9\\-]*' : undefined,
                    }}
                    sx={fieldSx}
                  />
                </Grid>
                {!readOnly ? (
                  <Grid item xs={12}>
                    <FormControlLabel
                      label="Camera upload"
                      labelPlacement="start"
                      control={
                        <Switch
                          checked={!!form.camera_upload}
                          onChange={(e) => setForm((f) => ({ ...f, camera_upload: e.target.checked }))}
                          color="primary"
                        />
                      }
                      sx={{ ml: 0, color: isDark ? drawerTheme.text : '#333' }}
                    />
                  </Grid>
                ) : null}

                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, color: isDark ? drawerTheme.text : '#333' }}
                  >
                    {getFrontDocumentLabel()}
                  </Typography>
                  {documentsLoading ? (
                    <Paper
                      sx={{
                        ...getGuestDrawerUploadPaperSx(isDark),
                        minHeight: 160,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'default',
                      }}
                    >
                      <CircularProgress size={28} sx={{ color: accent }} />
                    </Paper>
                  ) : (
                  <Paper
                    sx={getGuestDrawerUploadPaperSx(isDark)}
                    onClick={() => {
                      if (!readOnly) handleUploadClick('front');
                    }}
                  >
                    {form.id_front ? (
                      <Box>
                        <img
                          src={form.id_front}
                          alt="ID Front"
                          style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: 4 }}
                        />
                        {!readOnly ? (
                          <Button
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setForm((f) => ({ ...f, id_front: '' }));
                            }}
                            sx={{ mt: 1 }}
                          >
                            Remove
                          </Button>
                        ) : null}
                      </Box>
                    ) : (
                      <Box>
                        {readOnly ? (
                          <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
                            No document on file for this stay
                          </Typography>
                        ) : (
                          <>
                            {form.camera_upload ? (
                              <CameraAltIcon
                                sx={{ fontSize: 48, color: isDark ? drawerTheme.subtleText : '#666', mb: 1 }}
                              />
                            ) : (
                              <CloudUploadIcon
                                sx={{ fontSize: 48, color: isDark ? drawerTheme.subtleText : '#666', mb: 1 }}
                              />
                            )}
                            <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
                              {form.camera_upload
                                ? 'Click to capture from camera'
                                : 'Click to upload or drag and drop'}
                            </Typography>
                          </>
                        )}
                      </Box>
                    )}
                    <input
                      ref={fileInputFrontRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => handleFileUpload(e, 'front')}
                    />
                  </Paper>
                  )}
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography
                    variant="body2"
                    sx={{ mb: 1, fontWeight: 500, color: isDark ? drawerTheme.text : '#333' }}
                  >
                    {getBackDocumentLabel()}
                  </Typography>
                  {documentsLoading ? (
                    <Paper
                      sx={{
                        ...getGuestDrawerUploadPaperSx(isDark),
                        minHeight: 160,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'default',
                      }}
                    >
                      <CircularProgress size={28} sx={{ color: accent }} />
                    </Paper>
                  ) : (
                  <Paper
                    sx={getGuestDrawerUploadPaperSx(isDark)}
                    onClick={() => {
                      if (!readOnly) handleUploadClick('back');
                    }}
                  >
                    {form.id_back ? (
                      <Box>
                        <img
                          src={form.id_back}
                          alt="ID Back"
                          style={{ maxWidth: '100%', maxHeight: '180px', borderRadius: 4 }}
                        />
                        {!readOnly ? (
                          <Button
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              setForm((f) => ({ ...f, id_back: '' }));
                            }}
                            sx={{ mt: 1 }}
                          >
                            Remove
                          </Button>
                        ) : null}
                      </Box>
                    ) : (
                      <Box>
                        {readOnly ? (
                          <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
                            No document on file for this stay
                          </Typography>
                        ) : (
                          <>
                            {form.camera_upload ? (
                              <CameraAltIcon
                                sx={{ fontSize: 48, color: isDark ? drawerTheme.subtleText : '#666', mb: 1 }}
                              />
                            ) : (
                              <CloudUploadIcon
                                sx={{ fontSize: 48, color: isDark ? drawerTheme.subtleText : '#666', mb: 1 }}
                              />
                            )}
                            <Typography variant="body2" sx={{ color: isDark ? drawerTheme.subtleText : '#666' }}>
                              {form.camera_upload
                                ? 'Click to capture from camera'
                                : 'Click to upload or drag and drop'}
                            </Typography>
                          </>
                        )}
                      </Box>
                    )}
                    <input
                      ref={fileInputBackRef}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => handleFileUpload(e, 'back')}
                    />
                  </Paper>
                  )}
                </Grid>
              </Grid>
            </LocalizationProvider>
          </Box>
        </Box>

        <Box
          sx={{
            ...getBookingDrawerFooterSx(isDark),
            px: 1.75,
            py: 1.25,
            display: 'flex',
            gap: 1,
            width: '100%',
          }}
        >
          {mode === 'view' ? (
            <>
              <Button
                variant="outlined"
                onClick={onClose}
                sx={{
                  flex: 1,
                  borderRadius: 999,
                  py: 1.1,
                  fontWeight: 700,
                  borderColor: accent,
                  color: isDark ? accent : '#0f766e',
                }}
              >
                Close
              </Button>
              {allowEdit ? (
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => setMode('edit')}
                  sx={{
                    flex: 1,
                    borderRadius: 999,
                    py: 1.1,
                    fontWeight: 700,
                    backgroundColor: accent,
                    color: '#042f2e',
                    '&:hover': { backgroundColor: accent, filter: 'brightness(0.97)' },
                  }}
                >
                  Edit
                </Button>
              ) : null}
            </>
          ) : (
            <>
              <Button
                variant="outlined"
                onClick={() => {
                  if (initialMode === 'edit') onClose();
                  else {
                    setMode('view');
                    const next = buildFormFromCustomer(customer);
                    setForm(next);
                    setInitialFrontUrl(customer?.id_front_url || '');
                    setInitialBackUrl(customer?.id_back_url || '');
                    setError('');
                  }
                }}
                disabled={saving}
                sx={{
                  flex: 1,
                  borderRadius: 999,
                  py: 1.1,
                  fontWeight: 700,
                  borderColor: accent,
                  color: isDark ? accent : '#0f766e',
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                sx={{
                  flex: 1,
                  borderRadius: 999,
                  py: 1.1,
                  fontWeight: 700,
                  backgroundColor: accent,
                  color: '#042f2e',
                  '&:hover': { backgroundColor: accent, filter: 'brightness(0.97)' },
                }}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          )}
        </Box>
      </Box>

      {cameraOpen ? (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) stopCamera();
          }}
        >
          <Box
            sx={{
              position: 'relative',
              width: '100%',
              maxWidth: '800px',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: 2,
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography variant="h6" sx={{ color: 'white', mb: 2, fontWeight: 600 }}>
              {cameraType === 'front' ? 'Capture ID Front' : 'Capture ID Back'}
            </Typography>
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: '600px',
                borderRadius: 2,
                overflow: 'hidden',
                backgroundColor: 'black',
                border: '2px solid rgba(255, 255, 255, 0.3)',
              }}
            >
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: '100%', height: 'auto', display: 'block' }}
              />
              <canvas ref={canvasRef} style={{ display: 'none' }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 3, width: '100%' }}>
              <Button
                variant="contained"
                color="primary"
                onClick={capturePhoto}
                size="large"
                sx={{ minWidth: 150, py: 1.5, fontSize: '1rem', fontWeight: 600 }}
              >
                Capture Photo
              </Button>
              <Button
                variant="outlined"
                onClick={stopCamera}
                size="large"
                sx={{
                  minWidth: 150,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderColor: 'rgba(255, 255, 255, 0.5)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </Box>
      ) : null}
    </Drawer>
    <ForeignGuestDetailsDrawer
      open={foreignGuestDrawerOpen}
      onClose={() => setForeignGuestDrawerOpen(false)}
      hotelCountry={hotelCountry}
      guestCountry={form.country}
      initialDetails={foreignGuestDetails}
      onSave={(nextDetails) => {
        setForeignGuestDetails(nextDetails);
        if (nextDetails.passportNumber) {
          setForm((prev) => ({
            ...prev,
            document_type: 'passport',
            id_number: String(nextDetails.passportNumber).trim(),
          }));
        }
        setError('');
        setForeignGuestDrawerOpen(false);
      }}
    />
    <Snackbar
      open={cameraPermissionSnackbar.open}
      autoHideDuration={9000}
      onClose={() => setCameraPermissionSnackbar((s) => ({ ...s, open: false }))}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert
        onClose={() => setCameraPermissionSnackbar((s) => ({ ...s, open: false }))}
        severity="warning"
        sx={{ width: '100%' }}
      >
        {cameraPermissionSnackbar.message}
      </Alert>
    </Snackbar>
    </>
  );
}
